import {
  ErrorKey,
  isServerErrorResponse,
  TServerError,
  TServerErrorResponse,
} from "../../types/server-api/errors";
import { Box, Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { SimplePopupType } from "../SimplePopups/data";
import { useSimplePopups } from "../SimplePopups/SimplePopupProvider";
import { useLoadingWithOverlay } from "../loading-with-overlay/loading-with-overlay-provider";

export const createQueryErrorPopup = (
  error: any,
  errorTitle: string | null,
  createSimplePopup: (node: ReactNode, type: SimplePopupType) => void
) => {
  if (isServerErrorResponse(error)) {
    const additionalLabel = getServerErrorResponseLabel(error);
    const errorTitleNode = (
      <Stack gap={0.25}>
        <Box>{errorTitle}</Box>
        {additionalLabel}
      </Stack>
    );

    createSimplePopup(errorTitleNode, SimplePopupType.Error);
  } else {
    createSimplePopup(errorTitle, SimplePopupType.Error);
  }
};

export type TCallFunction<T = any> = (
  call: () => Promise<T>,
  errorTitle: string | null,
  successTitle: string | null,
  success: (data: T) => void,
  error?: (error: TServerErrorResponse) => void
) => Promise<T>;

export const useQueryWithPopupErrorHandling = () => {
  const { createSimplePopup } = useSimplePopups();
  const { showLoading, closeLoading } = useLoadingWithOverlay();

  return {
    call: async <T = any,>(
      query: () => Promise<T>,
      errorTitle: string | null = null,
      successTitle: string | null = null,
      success: (data: T) => void,
      errorCallback?: (err: TServerErrorResponse) => void,
      hideLoader?: boolean
    ) => {
      try {
        if (!hideLoader) {
          showLoading();
        }

        const res = await query();

        if (successTitle) {
          createSimplePopup(successTitle, SimplePopupType.Success);
        }
        success?.(res);
        return;
      } catch (error) {
        console.error(error);
        if (errorTitle) {
          if (isServerErrorResponse(error)) {
            const additionalLabel = getServerErrorResponseLabel(error);
            const errorTitleNode = (
              <Stack gap={0.25}>
                <Box>{errorTitle}</Box>
                {additionalLabel}
              </Stack>
            );

            createSimplePopup(errorTitleNode, SimplePopupType.Error);
          } else {
            createSimplePopup(errorTitle, SimplePopupType.Error);
          }
        }

        if (isServerErrorResponse(error)) {
          errorCallback?.(error);
        } else {
          errorCallback?.([]);
        }
      } finally {
        if (!hideLoader) {
          closeLoading();
        }
      }
    },
  };
};

function getServerErrorResponseLabel(
  errors: TServerErrorResponse
): ReactNode[] {
  const nodes: ReactNode[] = [];

  for (const error of errors) {
    const label = getErrorKeyLabel(error);

    if (label) {
      nodes.push(
        <Typography
          ml="1px"
          lineHeight={1}
          fontWeight={600}
          variant="subtitle2"
          display="block"
        >
          {label}
        </Typography>
      );
    }
  }

  return nodes;
}

function getErrorKeyLabel(error: TServerError) {
  switch (error.key) {
    case ErrorKey.INTERNAL_SERVER_ERROR:
      return "Interní chyba serveru";
    case ErrorKey.INVALID_PARAMS:
      return "Nevalidní parametry požadavku";
    case ErrorKey.UNAUTHORIZED:
      return "Uživatel nemá oprávnění k požadavku";
    case ErrorKey.OBJECT_NOT_FOUND:
      return "Hledaný objekt nebyl nalezen";
    default:
      return "";
  }
}
